import { useState } from 'react'
import { feedbackApi } from '../utils/api'

const defaultForm = {
  category: '',
  subject: '',
  message: '',
  screenshot_url: '',
}

const categories = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feedback', label: 'Product feedback' },
  { value: 'support', label: 'Support request' },
]

export default function Feedback() {
  const [form, setForm] = useState(defaultForm)
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState({ type: null, message: '' })

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitting(true)
    setStatus({ type: null, message: '' })

    try {
      await feedbackApi.submit(form)
      setStatus({
        type: 'success',
        message: 'Thanks! Your message was sent to the BoxHand team.',
      })
      setForm(defaultForm)
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto py-8">
        <div className="mb-8">
          <p className="text-sm uppercase tracking-wide text-primary-600 font-semibold">We’re listening</p>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">Send feedback or report an issue</h1>
          <p className="mt-3 text-gray-600">
            Tell us about bugs, product ideas, or anything else you need help with. We read every message.
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-3xl border border-gray-100 p-6 sm:p-10">
          {status.message && (
            <div
              className={`mb-6 rounded-lg px-4 py-3 text-sm ${
                status.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {status.message}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-semibold text-gray-900">
                What type of message is this? <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              >
                <option value="">Choose an option</option>
                {categories.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900">
                Subject (optional)
              </label>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="e.g. Payment schedule is off by one month"
                className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={6}
                placeholder="Describe the issue or request in as much detail as possible..."
                className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900">
                Screenshot or link (optional)
              </label>
              <input
                type="url"
                name="screenshot_url"
                value={form.screenshot_url}
                onChange={handleChange}
                placeholder="https://…"
                className="mt-1 block w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Paste a link to a screenshot, Loom video, or document if it helps explain the issue.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-full text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60"
              >
                {submitting ? 'Sending…' : 'Submit feedback'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

